/**
 * F-126 BGM slot geometry — pure (no React, no doc, no DOM), so the one thing
 * that is easy to get wrong is unit-tested rather than eyeballed in a browser.
 *
 * One slot per MOOD RUN (section run on a board divided before F-139),
 * positioned over the run's TOP cell in the BGM column. Deriving the rect from
 * the same `GridLayoutSnapshot` the table published — the way `sectionBands`
 * does — is what keeps the chips glued to their cells through pan, zoom, sort,
 * resize and F-26 row auto-grow.
 *
 * The slot spans the top cell exactly. It does NOT span the whole run: a run
 * can be forty rows long, and a chip stretched down all of them would bury
 * the script under a music player. The stems belong to the run; the place
 * you edit them is where the run starts.
 */
import { BGM_MIN_STEMS, type BgmCoverage, type BgmSectionRun } from "@kola/shared";

/**
 * The slice of `GridLayoutSnapshot` this module needs — named separately so
 * neither it nor its tests import from the grid (same split as
 * `SectionBandLayout` in features/sections).
 */
export interface BgmSlotLayout {
  rowIndex: ReadonlyMap<string, number>;
  colIndex: ReadonlyMap<string, number>;
  rowEdges: readonly number[];
  colEdges: readonly number[];
  originX: number;
  originY: number;
}

/** One run's stem slot, as a board-unit rect over the BGM column. */
export interface BgmSlot {
  /** Row the stems live on — the run's top row. */
  rowId: string;
  /** The run's label — the mood (or the section title on an older board). */
  title: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Stems on the top cell. */
  stems: number;
  /** True when the top cell already carries enough stems (`BGM_MIN_STEMS`). */
  covered: boolean;
  /** A stem sits lower in the run instead of on the top cell. */
  misplacedStem: boolean;
}

/**
 * Lay out one slot per run of `coverage`, in the BGM column.
 *
 * Returns [] when the BGM column is not on this sheet, or when the layout has
 * no columns/rows yet — the layer then renders nothing, which is correct
 * during the first paint and after someone deletes the column.
 *
 * A run whose top row is missing from the layout is SKIPPED, not clamped:
 * docs/07 C4 says an id absent from a present layout means the row is gone,
 * and a slot floating over an unrelated row would invite editing the wrong
 * run's music.
 */
export function bgmSlots(
  coverage: Pick<BgmCoverage, "runs">,
  bgmColId: string | undefined,
  layout: BgmSlotLayout,
): BgmSlot[] {
  if (bgmColId === undefined) return [];
  const col = layout.colIndex.get(bgmColId);
  if (col === undefined) return [];
  if (layout.colEdges.length < col + 2 || layout.rowEdges.length < 2) return [];

  const x = layout.originX + (layout.colEdges[col] as number);
  const w = (layout.colEdges[col + 1] as number) - (layout.colEdges[col] as number);

  const slots: BgmSlot[] = [];
  for (const run of coverage.runs) {
    const rowIndexValue = layout.rowIndex.get(run.topRowId);
    if (rowIndexValue === undefined) continue;
    if (rowIndexValue >= layout.rowEdges.length - 1) continue;
    const top = layout.rowEdges[rowIndexValue] as number;
    const bottom = layout.rowEdges[rowIndexValue + 1] as number;
    slots.push({
      rowId: run.topRowId,
      title: run.title,
      x,
      y: layout.originY + top,
      w,
      h: bottom - top,
      stems: run.stems,
      covered: run.covered,
      misplacedStem: run.misplacedStem,
    });
  }
  return slots;
}

/**
 * One line summarizing where the track stands — the panel's headline and the
 * chip's tooltip both read it, so the two can never disagree.
 */
export function coverageSummary(coverage: BgmCoverage): string {
  if (coverage.total === 0) {
    return "Divide the script first — each mood gets a music slot.";
  }
  // F-141: on a Shorts board the runs are per short (and per mood inside
  // one), so "moods" would undercount what the designer is looking at.
  const unit = coverage.runs.some((run) => run.group !== undefined) ? "slots" : "moods";
  if (coverage.ready) {
    return `All ${coverage.total} ${unit} have ${BGM_MIN_STEMS}+ stems.`;
  }
  return `${coverage.covered} of ${coverage.total} ${unit} have ${BGM_MIN_STEMS}+ stems.`;
}

/** The runs still short of stems, in script order. */
export function missingRuns(coverage: BgmCoverage): BgmSectionRun[] {
  return coverage.runs.filter((run) => !run.covered);
}
